import { CircularProgress, styled } from '@mui/material'
import { circularProgressClasses } from '@mui/material/CircularProgress'
import { UserListItemSkeleton } from '$common/user-list-item'
import { RoomAppBarInfo } from '$common/room-app-bar/room-app-bar-info'
import { openHallProfile } from '$logic/router'
import { useHall, useHideMemberCount } from '$logic/hall/react-hook'
import { toLocale } from '$func/text'
import { HallAppBarCallElement } from '$common/room-app-bar/components/hall-app-bar-call-element'
import { t } from '$i18n'
import { HallAvatar } from '$common/avatar'
import { stringToColor } from '$func/string-to-color'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import hostConf from '$config/frontend'
import { useStartCall } from './react-hooks'
import { ROOM_APP_BAR_AVATAR_SIZE } from '../conf'
import { VisibilityOffOutlined } from '@mui/icons-material'
import { useWorkspaceColorByRoomId } from '$logic/workspace'

const hidePrivateHallMemberCount = hostConf.VITE_HIDE_PRIVATE_HALL_MEMBER_COUNT

const StyledCircularProgress = styled(CircularProgress)`
  color: ${({ theme }) => theme.palette.neutral700};
  margin-left: 4px;
  & .${circularProgressClasses.circle} {
    stroke-linecap: round;
  }
`

export function ChannelAppBar({ roomId }: { roomId: string }) {
  const { startingCall, openChooseCallDialog, call, canStartCall } =
    useStartCall({ roomId, hallType: 'CHANNEL' })

  const { isAdmin } = useClientUserPermissions(roomId) || {}
  const { entity: hall } = useHall(roomId)

  const hideMemberCount = useHideMemberCount(roomId)

  const backgroundColor = useWorkspaceColorByRoomId(roomId)
  if (!hall) return <UserListItemSkeleton />
  const avatarBackgroundColor = stringToColor(hall.name, roomId)

  return (
    <RoomAppBarInfo
      backgroundColor={backgroundColor}
      onClick={() => openHallProfile(hall.roomId)}
      avatarElement={
        <HallAvatar
          size={ROOM_APP_BAR_AVATAR_SIZE}
          roomId={hall.roomId}
          withStory
        />
      }
      avatarSrc=""
      primaryText={hall.name}
      secondaryText={
        <>
          {hideMemberCount && isAdmin && (
            <VisibilityOffOutlined sx={{ width: '16px', height: '16px' }} />
          )}
          {!hidePrivateHallMemberCount || !hideMemberCount || isAdmin
            ? `${toLocale(hall.membersCount)} ${t('profile.hall.subscriber')}`
            : ''}
        </>
      }
      verified={hall.verified}
      avatarBackgroundColor={avatarBackgroundColor}
      deleted={!!hall?.deletedAt}
      callElement={
        call || !canStartCall ? null : startingCall ? (
          <StyledCircularProgress size={24} />
        ) : (
          <HallAppBarCallElement
            onClick={openChooseCallDialog}
            roomType="CHANNEL"
            background={backgroundColor}
          />
        )
      }
    />
  )
}
